import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { SharePointTitle } from 'common/enums';
import type { ResponseModel } from 'common/interfaces';
import { useFetch } from 'hooks/use-fetch/use-fetch';
import { useAllDocumentTypes } from 'hooks/react-query/use-all-document-types';
import { pollForFileValidation } from './use-file-validation-polling';

interface UploadIncidentFilesParams {
  data: Record<SharePointTitle, File[]>;
  incidentId: string;
}

interface UploadIncidentFilesResponse {
  isSuccess: boolean;
  isError: boolean;
  errorMessages: string[];
}

interface UploadFilesApiResponse {
  statusQueryGetUri: string;
}

export const useUploadIncidentFiles = () => {
  const { getDocumentTypeId } = useAllDocumentTypes();

  const [uploadError, setUploadError] = useState<string | null>(null);

  const { fetch: uploadFetch } = useFetch<ResponseModel<UploadFilesApiResponse>>({
    isShowLoading: false,
    isShowErrorSnackbar: false,
    isShowSuccessSnackbar: false
  });

  const { fetch: statusFetch } = useFetch({
    isShowLoading: false,
    isShowErrorSnackbar: false,
    isShowSuccessSnackbar: false
  });

  const uploadSingleFile = async (file: File, documentType: string, incidentId: string): Promise<void> => {
    const uploadResponse = await uploadFetch({
      requestConfig: {
        method: 'POST',
        url: 'incidents/UploadFiles',
        data: buildUploadFilesPayload({
          documentTypeId: getDocumentTypeId(documentType),
          incidentId,
          file
        })
      }
    });

    if (!uploadResponse.data.isSuccess) {
      throw new Error(buildErrorMessage(documentType, uploadResponse.data.messages));
    }

    const { statusQueryGetUri } = uploadResponse.data.data;
    await pollForFileValidation(statusQueryGetUri, statusFetch);
  };

  const { mutateAsync: uploadIncidentFiles } = useMutation({
    onMutate: () => {
      setUploadError(null);
    },
    mutationFn: async (params: UploadIncidentFilesParams) => {
      const { data, incidentId } = params;

      const result: UploadIncidentFilesResponse = {
        errorMessages: [],
        isError: false,
        isSuccess: true
      };

      for (const [documentType, files] of Object.entries(data)) {
        if (!files.length) continue;

        for (const file of files) {
          try {
            await uploadSingleFile(file, documentType, incidentId);
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : buildErrorMessage(documentType);

            result.isError = true;
            result.isSuccess = false;
            result.errorMessages.push(errorMessage);
            setUploadError(errorMessage);

            return result;
          }
        }
      }

      return result;
    }
  });

  return {
    uploadIncidentFiles,
    uploadError
  };
};

interface BuildUploadFilesPayloadParams {
  file: File;
  incidentId: string;
  documentTypeId: string;
}

function buildUploadFilesPayload(params: BuildUploadFilesPayloadParams): FormData {
  const { documentTypeId, incidentId, file } = params;

  const data = new FormData();

  data.append('Files', file, file.name);
  data.append('IncidentId', incidentId);
  data.append('DocumentTypeId', documentTypeId);

  return data;
}

function buildErrorMessage(documentType: string, errorMessages?: ResponseModel['messages']): string {
  const baseMessage = `${documentType} konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.`;

  if (errorMessages && errorMessages.length > 0) {
    const innerMessage = errorMessages
      .map(m => m.message)
      .filter(Boolean)
      .join('\n');

    return innerMessage ? `${baseMessage} (${innerMessage})` : baseMessage;
  }

  return baseMessage;
}
